/**
 * FRONT-1644: pure extraction of a custom tool's produced-file list from a
 * chat tool-call `output`. Dependency-free (no React) so it can be unit
 * tested directly, matching the `hasTopLevelError`/`hasNoChange` precedent
 * in `message-bubble.tsx` — a tool result can arrive as either a parsed
 * object or a JSON string depending on how the message was hydrated, so
 * both are handled the same way here.
 *
 * The backend (`src/tools/custom-tool-outputs.ts`) puts `outputFiles`
 * FIRST on the result object as insurance against a BACKGROUND/CRON tool
 * call's result passing through the 24,000-char `truncateToolResult`
 * (`src/agents/service/restate.ts`) — the interactive chat path
 * (`chat-proxy.ts`) never truncates. This reader doesn't care about key
 * order either way, only that the array is there.
 */

export interface CustomToolOutputFile {
  filename: string;
  sizeBytes: number;
  contentType?: string;
  /** Present only for a persisted file (the chat path always persists). */
  fileId?: string;
  /** The download URL — present only for a persisted file. Always
   *  `/api/files/<id>/raw` in practice (server-generated); enforced below
   *  as a structural guard, not just an assumption. */
  url?: string;
  /** Present when this entry couldn't be surfaced (too large, read/save
   *  failure) — never paired with `url`. */
  skipped?: string;
}

/** The one path prefix a produced file's download `url` is ever allowed to
 *  have. FRONT-1644 review finding #9: server-generated today, but an href
 *  built from an unchecked string is a latent open-redirect/XSS-adjacent
 *  risk the moment that stops being true — this makes it structural rather
 *  than assumed. */
const OUTPUT_FILE_URL_PREFIX = '/api/files/';

/** True when `entry` is shaped closely enough to render safely — has a
 *  filename and size, and either a download url (which must start with
 *  `/api/files/`) or a skipped reason. Never render a broken or unsafe
 *  link: an entry with neither, or with an out-of-shape url, is dropped. */
function isRenderableOutputFile(entry: unknown): entry is CustomToolOutputFile {
  if (typeof entry !== 'object' || entry === null) return false;
  const e = entry as Record<string, unknown>;
  if (typeof e.filename !== 'string' || !e.filename) return false;
  if (typeof e.sizeBytes !== 'number') return false;
  const hasUrl = typeof e.url === 'string' && e.url.startsWith(OUTPUT_FILE_URL_PREFIX);
  const hasSkipped = typeof e.skipped === 'string' && e.skipped.length > 0;
  return hasUrl || hasSkipped;
}

/**
 * Extract the produced-file list from a tool's `output`, or `[]` when there
 * is none. This is the one decision `ToolCallCard` needs to make about
 * whether/what to render for output files — kept here, pure, so it's
 * testable without rendering the card.
 */
export function getCustomToolOutputFiles(output: unknown): CustomToolOutputFile[] {
  let candidate: unknown = output;
  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return [];
    }
  }
  if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) return [];
  const raw = (candidate as Record<string, unknown>).outputFiles;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.filter(isRenderableOutputFile);
}

/** True when at least one entry actually has a download url — i.e. at
 *  least one file was really saved to Files. FRONT-1644 review finding
 *  #10: the "Saved to your Files" caption may only claim this when it's
 *  true; a run where every file was skipped must not say it. */
export function hasSavedOutputFile(files: CustomToolOutputFile[]): boolean {
  return files.some((f) => Boolean(f.url));
}

/**
 * FRONT-2035: true when `f` has a download url AND a content type that
 * marks it a picture — the one signal both surfaces (the desktop
 * `ToolCallCard` strip and the phone's `PwaTraceCard` strip) use to decide
 * "show a thumbnail" vs "show a plain download chip". Deliberately broad
 * (`image/*`, not the raw route's own narrower inline allow-list in
 * `src/files/routes.ts`) — a content type the raw route won't actually
 * serve inline (e.g. `image/svg+xml`, forced to `application/octet-stream`
 * there) still reaches here, and the `<img>` itself fails to decode, which
 * both callers already treat as a fallback-to-download-chip case rather
 * than something this predicate needs to special-case.
 */
export function isImageOutputFile(
  f: CustomToolOutputFile,
): f is CustomToolOutputFile & { url: string } {
  return typeof f.url === 'string' && typeof f.contentType === 'string' && f.contentType.startsWith('image/');
}

/** Human-readable byte size — same thresholds/format as `routes/files.tsx`'s
 *  `formatBytes` so a file's size reads consistently across the app. */
export function formatOutputFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
